import * as d3 from 'd3';

import { OverlaySeries } from './OverlaySeries';
import { IOverlayModel } from './IOverlayModel';
import { SvgManager } from '../../charting/SvgManager';
import { ILogger, LoggerFactory } from '../../helpers/helpers';

export class BackgroundOverlay {
    private _backgroundUrl: string;
    private _container: d3.Selection<any>;
    private _fitScale: number;
    private _group: d3.Selection<{}>;
    private _height: number;
    private _isGridVisible = true;
    private _isReadOnly: boolean;
    private _isZoomEnabled: boolean;
    private _logger: ILogger;
    private _originalHeight: number;
    private _originalWidth: number;
    private _overlay: OverlaySeries;
    private _series: IOverlayModel[];
    private _width: number;
    private _xAxis: d3.svg.Axis;
    private _xAxisGroup: d3.Selection<any>;
    private _xScale: d3.scale.Linear<number, number>;
    private _yAxis: d3.svg.Axis;
    private _yAxisGroup: d3.Selection<any>;
    private _yScale: d3.scale.Linear<number, number>;
    private _zoom: d3.behavior.Zoom<{}>;
    private margin = { top: 0, right: 0, bottom: 0, left: 0 };

    constructor(manager: SvgManager) {
        this._logger = LoggerFactory.getLogger('WideTech.Framework.Diagrams.backgroundOverlay');
        const container = manager.container();
        const size = manager.getSize();
        this._width = size.width;
        this._height = size.height;
        this._container = container;
        this.init();
        manager.addResizeFunction((w, h) => this.resize(w, h));
    }

    private resize(w: number, h: number) {
        this._width = w;
        this._height = h;
        const chartWidth = w - this.margin.left - this.margin.right;
        const chartHeight = h - this.margin.top - this.margin.bottom;
        if (!this._originalHeight) {
            this._originalHeight = chartHeight;
        }
        if (!this._originalWidth) {
            this._originalWidth = chartWidth;
        }
        this.initZoomAndAxis();

        this._xScale.range([0, chartWidth]);
        this._xAxis.tickSize(-chartHeight);
        this._xAxisGroup.call(this._xAxis);
        this._yScale.range([chartHeight, 0]);
        this._yAxis.tickSize(-chartWidth);
        this._yAxisGroup.call(this._yAxis);

        this._group
            .select('g.content')
            .select('image.background')
            .attr('width', this._originalWidth)
            .attr('height', this._originalHeight);

        this._zoom.scale(this._fitScale);
        this._zoom.event(this._group);
    }

    private init(): void {
        this._logger.debug('Init');
        const content = this.reset();
        this._overlay = new OverlaySeries(content, this._isReadOnly);
        if (this._series) {
            this.updateSeries(this._series);
        }
    }

    private reset(): d3.Selection<any> {
        this._logger.debug('Reset');
        this._container.select('.background-overlay').remove();

        this._group = this._container
            .append('g')
            .classed('background-overlay', true)
            .attr('transform', `translate(${this.margin.left},${this.margin.top})`);

        const dataGroup = this._group
            .append('g')
            .attr('class', 'overlay');

        const content = dataGroup
            .append('g')
            .attr('class', 'content');

        const chartWidth = this._width - this.margin.left - this.margin.right;
        const chartHeight = this._height - this.margin.top - this.margin.bottom;

        content
            .append('image')
            .classed('background', true)
            .attr('width', chartWidth)
            .attr('height', chartHeight)
            .attr('href', this._backgroundUrl);

        return content;
    }

    initZoomAndAxis() {
        const x = this.initXAxis();
        const y = this.initYAxis();
        this._fitScale = Math.min(this._height / this._originalHeight, this._width / this._originalWidth);
        const lowRange = Math.min(0.5, this._fitScale);
        const highRange = Math.max(2, this._fitScale);

        this._zoom = d3.behavior.zoom()
            .x(x)
            .y(y)
            .scaleExtent([lowRange, highRange])
            .size([this._width, this._height])
            .on('zoom', () => this.zoomed());

        this._group.call(this._zoom);

        if (!this._isZoomEnabled && this._zoom) {
            this.fit();
            this._zoom.on('zoom', null);
            this._group.on('.zoom', null);
        }

        this._overlay.xScale(this._xScale);
        this._overlay.yScale(this._yScale);

        this._xAxisGroup.style('visibility', this._isGridVisible ? 'visible' : 'hidden');
        this._yAxisGroup.style('visibility', this._isGridVisible ? 'visible' : 'hidden');
    }

    private initXAxis(): d3.scale.Linear<number, number> {
        const xScale = d3.scale.linear()
            .domain([0, this._originalWidth])
            .range([0, this._width]);

        this._xAxis = d3.svg.axis()
            .scale(xScale)
            .orient('top')
            .tickSize(-this._height)
            .ticks(10)
            .tickFormat(''); // no label

        this._group.select('.x.axis').remove();
        this._xAxisGroup = this._group
            .append('g')
            .attr('class', 'x axis')
            .call(this._xAxis);
        this._xScale = xScale;
        return xScale;
    }

    private initYAxis(): d3.scale.Linear<number, number> {
        const yScale = d3.scale.linear()
            .domain([this._originalHeight, 0])
            .range([this._height, 0]);

        this._yAxis = d3.svg.axis()
            .scale(yScale)
            .orient('left')
            .ticks(5)
            .tickSize(-this._width)
            .tickFormat(''); // no-label

        this._group.select('.y.axis').remove();
        this._yAxisGroup = this._group
            .append('g')
            .attr('class', 'y axis')
            .call(this._yAxis);
        this._yScale = yScale;
        return yScale;
    }

    private zoomed(): boolean {
        const t = (d3.event as any).translate;
        const s = (d3.event as any).scale;

        t[0] = Math.min(0, Math.max(-(this._originalWidth * s - this._width), t[0]));
        t[1] = Math.min(0, Math.max(-(this._originalHeight * s - this._height), t[1]));

        this.applyZoom(t, s);

        // calculate new center
        const center: [number, number] = [
            (((this._width / 2) - t[0]) / s),
            (((this._height / 2) - t[1]) / s)
        ];
        this._overlay.updateZoomScale(s, center);
        return false;
    }

    private applyZoom(translate: [number, number], scale: number) {
        if (isNaN(translate[0]) || isNaN(translate[1])) {
            translate = [0, 0];
        }
        if (isNaN(scale)) {
            scale = 0;
        }

        this._zoom.translate(translate);

        this._group.select('.content')
            .attr('transform', `translate(${translate})scale(${scale})`);
        this._group.select('.content')
            .select('rect')
            .style('stroke-width', 1 / scale);

        this._xAxisGroup.call(this._xAxis);
        this._yAxisGroup.call(this._yAxis);
    }

    updateSeries(data: IOverlayModel[]) {
        this._logger.debug(`updateSeries ${data.length}`);
        this._series = data;
        if (this._overlay) {
            this._overlay.update(data);
        }
    }

    updateBackground(url: string, measure: any, callback?: () => void) {
        this._logger.debug(`updateBackground - url: ${url}, measure: ${measure}`);
        measure.onload = () => {
            this._originalWidth = measure.clientWidth;
            this._originalHeight = measure.clientHeight;
            this._backgroundUrl = url;
            this._logger.debug(`onload ${this._originalWidth} ${this._originalHeight}`);

            this.init();
            this.resize(this._width, this._height);
            if (callback) {
                callback();
            }
        };
    }

    isGridVisible(value?: boolean): boolean {
        if (arguments.length) {
            this._isGridVisible = value;
            if (this._xAxisGroup) {
                this._xAxisGroup.style('visibility', value ? 'visible' : 'hidden');
            }
            if (this._yAxisGroup) {
                this._yAxisGroup.style('visibility', value ? 'visible' : 'hidden');
            }
        }
        return this._isGridVisible;
    }

    isReadOnly(newValue: boolean) {
        this._isReadOnly = newValue;
        this._overlay.isReadOnly(newValue);
    }

    isZoomEnabled(newValue: boolean) {
        this._isZoomEnabled = newValue;
        this.initZoomAndAxis();
    }

    fit() {
        this._zoom.scale(this._fitScale);
        this._zoom.event(this._group);
    }

}